/**
 * F-30: Status UX Standardization
 * 
 * Defines standardized status metadata for cameras, policies, and deployments.
 * Each status includes:
 * - 1-line user-facing reason
 * - primary remediation action
 * - color coding
 */

export type CameraHealthState = 'READY' | 'DEGRADED' | 'OFFLINE' | 'AUTH_FAILED' | 'STREAM_ERROR';
export type PolicyDeploymentState = 'DRAFT' | 'DEPLOYING' | 'DEPLOYED' | 'FAILED' | 'FAILED_TIMEOUT';
export type DeploymentStatus = 'deploying' | 'active' | 'failed' | 'failed_timeout';
export type DeviceLifecycleState =
  | 'decommissioned'
  | 'decommission_incomplete'
  | 'commissioning'
  | 'commissioned'
  | 'deploying'
  | 'deployed'
  | 'recovering'
  | 'offline'
  | 'attention'
  | 'unknown';
export type PolicyTroubleshootingState =
  | 'healthy'
  | 'draft'
  | 'pending_deploy'
  | 'deploying'
  | 'deployment_failed'
  | 'deployment_timeout'
  | 'missing_target'
  | 'orphaned_camera'
  | 'lint_blocked'
  | 'zone_issue'
  | 'attention';

export interface StatusMetadata {
  label: string;
  color: 'success' | 'warning' | 'error' | 'default' | 'info';
  reason: string;  // 1-line user-facing reason
  remediation: string;  // Primary remediation action
  remediationAction?: {
    type: 'preflight' | 'fix_credentials' | 'restart_pipeline' | 'redeploy' | 'edit_policy' | 'open_details' | 'none';
    label: string;
    path?: string;  // Optional deep link
  };
}

export const cameraHealthMetadata: Record<CameraHealthState, StatusMetadata> = {
  READY: {
    label: 'Ready',
    color: 'success',
    reason: 'Camera is online and streaming correctly',
    remediation: 'No action needed',
    remediationAction: { type: 'none', label: 'No action needed' },
  },
  DEGRADED: {
    label: 'Degraded',
    color: 'warning',
    reason: 'Camera is online but validation is stale or performance is reduced',
    remediation: 'Run Preflight to verify stream and diagnose issues',
    remediationAction: {
      type: 'preflight',
      label: 'Run Preflight',
      path: '/preflight',
    },
  },
  OFFLINE: {
    label: 'Offline',
    color: 'error',
    reason: 'Device is offline or unreachable',
    remediation: 'Check device connectivity and edge agent status',
    remediationAction: {
      type: 'restart_pipeline',
      label: 'Check Device Status',
      path: '/devices',
    },
  },
  AUTH_FAILED: {
    label: 'Auth Failed',
    color: 'error',
    reason: 'RTSP authentication failed - invalid credentials',
    remediation: 'Fix RTSP credentials in camera settings',
    remediationAction: {
      type: 'fix_credentials',
      label: 'Fix Credentials',
      path: '/cameras',
    },
  },
  STREAM_ERROR: {
    label: 'Stream Error',
    color: 'error',
    reason: 'Video stream decode or pipeline error',
    remediation: 'Run Preflight to diagnose and fix stream issues',
    remediationAction: {
      type: 'preflight',
      label: 'Run Preflight',
      path: '/preflight',
    },
  },
};

export const policyDeploymentMetadata: Record<PolicyDeploymentState, StatusMetadata> = {
  DRAFT: {
    label: 'Draft',
    color: 'default',
    reason: 'Policy not deployed yet or not active',
    remediation: 'Deploy policy to apply to edge devices',
    remediationAction: {
      type: 'redeploy',
      label: 'Deploy Policy',
      path: '/policies',
    },
  },
  DEPLOYING: {
    label: 'Deploying',
    color: 'warning',
    reason: 'Policy deployment in progress, awaiting edge ACK/NACK',
    remediation: 'Wait for deployment to complete or check deployment status',
    remediationAction: {
      type: 'none',
      label: 'Check Status',
      path: '/deployments',
    },
  },
  DEPLOYED: {
    label: 'Deployed',
    color: 'success',
    reason: 'Policy successfully applied on edge device',
    remediation: 'No action needed',
    remediationAction: { type: 'none', label: 'No action needed' },
  },
  FAILED: {
    label: 'Failed',
    color: 'error',
    reason: 'Edge device rejected the deployment',
    remediation: 'Review error details and re-deploy after fixing issues',
    remediationAction: {
      type: 'redeploy',
      label: 'Re-deploy',
      path: '/policies',
    },
  },
  FAILED_TIMEOUT: {
    label: 'Failed (Timeout)',
    color: 'error',
    reason: 'No ACK received from edge device within timeout period',
    remediation: 'Check device connectivity and re-deploy',
    remediationAction: {
      type: 'redeploy',
      label: 'Re-deploy',
      path: '/policies',
    },
  },
};

export const deploymentStatusMetadata: Record<DeploymentStatus, StatusMetadata> = {
  deploying: {
    label: 'Deploying',
    color: 'warning',
    reason: 'Deployment in progress, awaiting edge ACK/NACK',
    remediation: 'Wait for deployment to complete',
    remediationAction: {
      type: 'none',
      label: 'Check Status',
    },
  },
  active: {
    label: 'Active',
    color: 'success',
    reason: 'Deployment successfully applied on edge device',
    remediation: 'No action needed',
    remediationAction: { type: 'none', label: 'No action needed' },
  },
  failed: {
    label: 'Failed',
    color: 'error',
    reason: 'Edge device rejected the deployment',
    remediation: 'Review error details and re-deploy after fixing issues',
    remediationAction: {
      type: 'redeploy',
      label: 'Re-deploy',
    },
  },
  failed_timeout: {
    label: 'Failed (Timeout)',
    color: 'error',
    reason: 'No ACK received from edge device within timeout period',
    remediation: 'Check device connectivity and re-deploy',
    remediationAction: {
      type: 'redeploy',
      label: 'Re-deploy',
    },
  },
};

export const deviceLifecycleMetadata: Record<DeviceLifecycleState, StatusMetadata> = {
  decommissioned: {
    label: 'Decommissioned',
    color: 'default',
    reason: 'Unit has been decommissioned and is claimable for reprovisioning.',
    remediation: 'Recommission only if this unit should return to service',
    remediationAction: { type: 'none', label: 'Review status' },
  },
  decommission_incomplete: {
    label: 'Decommission Incomplete',
    color: 'warning',
    reason: 'Cloud started decommission, but edge proof/cleanup is not complete yet.',
    remediation: 'Finish edge cleanup or retry the decommission flow',
    remediationAction: { type: 'open_details', label: 'Review details' },
  },
  commissioning: {
    label: 'Commissioning',
    color: 'info',
    reason: 'Unit is in claim/bring-up flow and is not fully ready yet.',
    remediation: 'Complete onboarding, desired-state sync, and gate validation',
    remediationAction: { type: 'preflight', label: 'Open Bring-Up', path: '/preflight' },
  },
  commissioned: {
    label: 'Commissioned',
    color: 'success',
    reason: 'Unit is activated and ready for normal operations.',
    remediation: 'No action needed',
    remediationAction: { type: 'none', label: 'No action needed' },
  },
  deploying: {
    label: 'Deploying',
    color: 'warning',
    reason: 'A rollout is in progress and edge convergence is still pending.',
    remediation: 'Wait for convergence or inspect deployment details',
    remediationAction: { type: 'open_details', label: 'View deployments', path: '/deployments' },
  },
  deployed: {
    label: 'Deployed',
    color: 'success',
    reason: 'Latest desired rollout is active on this unit.',
    remediation: 'No action needed',
    remediationAction: { type: 'none', label: 'No action needed' },
  },
  recovering: {
    label: 'Recovering',
    color: 'warning',
    reason: 'The latest rollout degraded or failed and recovery/rollback is in progress.',
    remediation: 'Review rollback and recovery status before the next change',
    remediationAction: { type: 'open_details', label: 'Open diagnostics', path: '/diagnostics' },
  },
  offline: {
    label: 'Offline',
    color: 'error',
    reason: 'Unit heartbeat is stale or the edge is unreachable.',
    remediation: 'Check connectivity, power, and edge runtime health',
    remediationAction: { type: 'preflight', label: 'Open diagnostics', path: '/diagnostics' },
  },
  attention: {
    label: 'Needs Attention',
    color: 'warning',
    reason: 'Unit signals are mixed or incomplete and need operator review.',
    remediation: 'Inspect provisioning, deployment, and diagnostics signals together',
    remediationAction: { type: 'open_details', label: 'Review details' },
  },
  unknown: {
    label: 'Unknown',
    color: 'default',
    reason: 'Lifecycle state is unavailable.',
    remediation: 'Refresh the unit list or inspect diagnostics',
    remediationAction: { type: 'open_details', label: 'Review details' },
  },
};

export const policyTroubleshootingMetadata: Record<PolicyTroubleshootingState, StatusMetadata> = {
  healthy: {
    label: 'Healthy',
    color: 'success',
    reason: 'Policy is active and applied on the target edge device.',
    remediation: 'No action needed',
    remediationAction: { type: 'none', label: 'No action needed' },
  },
  draft: {
    label: 'Draft',
    color: 'default',
    reason: 'Policy is saved but not yet active on the edge.',
    remediation: 'Activate or deploy when ready',
    remediationAction: { type: 'edit_policy', label: 'Review policy' },
  },
  pending_deploy: {
    label: 'Ready to deploy',
    color: 'info',
    reason: 'Policy is active but has not been deployed to the edge yet.',
    remediation: 'Deploy to push rules to edge devices',
    remediationAction: { type: 'redeploy', label: 'Deploy Policy', path: '/policies' },
  },
  deploying: {
    label: 'Deploying',
    color: 'warning',
    reason: 'Deployment is in progress and waiting for edge confirmation.',
    remediation: 'Wait for completion or open deployment details',
    remediationAction: { type: 'open_details', label: 'View details' },
  },
  deployment_failed: {
    label: 'Deploy failed',
    color: 'error',
    reason: 'Edge rejected the deployment or the compiled bundle could not be applied.',
    remediation: 'Inspect the failure reason and fix the policy before retrying',
    remediationAction: { type: 'open_details', label: 'Fix issue' },
  },
  deployment_timeout: {
    label: 'Deploy timeout',
    color: 'error',
    reason: 'No ACK arrived from the edge before the timeout window expired.',
    remediation: 'Check device connectivity and retry deployment',
    remediationAction: { type: 'preflight', label: 'Check connectivity', path: '/preflight' },
  },
  missing_target: {
    label: 'Target missing',
    color: 'error',
    reason: 'The policy has no valid camera/device binding.',
    remediation: 'Rebind the policy to a valid camera before activating or deploying',
    remediationAction: { type: 'edit_policy', label: 'Fix target' },
  },
  orphaned_camera: {
    label: 'Camera missing',
    color: 'error',
    reason: 'The policy still references a camera that is no longer registered.',
    remediation: 'Select a replacement camera or delete the policy',
    remediationAction: { type: 'edit_policy', label: 'Reassign policy' },
  },
  lint_blocked: {
    label: 'Lint blocked',
    color: 'error',
    reason: 'The policy cannot activate or deploy until validation issues are fixed.',
    remediation: 'Open details and resolve the reported validation errors',
    remediationAction: { type: 'open_details', label: 'Review issues' },
  },
  zone_issue: {
    label: 'Zone issue',
    color: 'error',
    reason: 'The policy references missing, stale, or unconverged zone state.',
    remediation: 'Repair zone bindings or convergence before retrying',
    remediationAction: { type: 'open_details', label: 'Review zones' },
  },
  attention: {
    label: 'Needs attention',
    color: 'warning',
    reason: 'The policy has a deployment or integrity issue that needs operator review.',
    remediation: 'Open details and choose the next safe action',
    remediationAction: { type: 'open_details', label: 'Review details' },
  },
};

/**
 * Get status metadata for a camera health state
 */
export function getCameraHealthMetadata(state?: CameraHealthState): StatusMetadata {
  if (!state) {
    return {
      label: 'Unknown',
      color: 'default',
      reason: 'Status unknown',
      remediation: 'Run Preflight to check camera status',
      remediationAction: { type: 'preflight', label: 'Run Preflight', path: '/preflight' },
    };
  }
  return cameraHealthMetadata[state] || cameraHealthMetadata.DEGRADED;
}

/**
 * Get status metadata for a policy deployment state
 */
export function getPolicyDeploymentMetadata(state?: PolicyDeploymentState): StatusMetadata {
  if (!state) {
    return {
      label: 'Unknown',
      color: 'default',
      reason: 'Deployment status unknown',
      remediation: 'Check deployment status',
      remediationAction: { type: 'none', label: 'Check Status', path: '/deployments' },
    };
  }
  return policyDeploymentMetadata[state] || policyDeploymentMetadata.DRAFT;
}

/**
 * Get status metadata for a deployment status
 */
export function getDeploymentStatusMetadata(status?: string): StatusMetadata {
  const normalized = (status || '').toLowerCase() as DeploymentStatus;
  if (!normalized || !deploymentStatusMetadata[normalized]) {
    return {
      label: 'Unknown',
      color: 'default',
      reason: 'Deployment status unknown',
      remediation: 'Check deployment status',
      remediationAction: { type: 'none', label: 'Check Status' },
    };
  }
  return deploymentStatusMetadata[normalized];
}

export function getDeviceLifecycleMetadata(state?: string): StatusMetadata {
  const normalized = (state || '').toLowerCase() as DeviceLifecycleState;
  if (!normalized || !deviceLifecycleMetadata[normalized]) {
    return deviceLifecycleMetadata.unknown;
  }
  return deviceLifecycleMetadata[normalized];
}

export function getPolicyTroubleshootingMetadata(state?: PolicyTroubleshootingState): StatusMetadata {
  if (!state) return policyTroubleshootingMetadata.attention;
  return policyTroubleshootingMetadata[state] || policyTroubleshootingMetadata.attention;
}

